import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { meSelector } from "../redux/globalSlice"
import { LINK_ILOK_STATE } from "./IlokAccountsCard"
import { ilokIconBlack } from "./widgets/pillIcons"
import { backdropDismiss } from "../utils/backdropDismiss"

// The iLok reminders of the Offers page, for users with NO iLok account row
// yet (a pending link counts as engaged): licenses held in a linked iLok
// account become native entitlements automatically and count as upgrade
// credit right away, so an unlinked user is looking at a HIGHER upgrade
// price than they could get. Two touch points, both per-user in
// localStorage (a fresh browser shows them once more — acceptable):
// - IlokUpgradeBanner above the upgrade cards, dismissible for good (×);
//   following the CTA does NOT dismiss it — if they abandon the linking it
//   returns, and once an account row exists it disappears by itself.
// - IlokUpgradeConfirmDialog on the Upgrade click, shown ONCE per user
//   however it is closed: link, continue, or dismiss.
// Both CTAs land on the Account page with the link dialog already open
// (router-state handoff read by IlokAccountsCard).
const dismissedKey = (userId: string) => `ilokUpgradeHintDismissed:${userId}`
const confirmSeenKey = (userId: string) => `ilokUpgradeConfirmSeen:${userId}`

// Per-user localStorage flags shared by the iLok reminders (here and the
// Dashboard's LinkIlokCard).
export function flagSet(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null
  } catch {
    return true // storage unavailable (private mode etc.) — behave as seen, never nag
  }
}

export function setFlag(key: string) {
  try {
    localStorage.setItem(key, new Date().toISOString())
  } catch {
    // best-effort — worst case the reminder shows again next visit
  }
}

// Whether the confirm step is still owed to this user (the caller gates on
// "no iLok account" itself).
export function useIlokUpgradeConfirmPending(): boolean {
  const me = useSelector(meSelector)?.data
  return me !== undefined && !flagSet(confirmSeenKey(me.id))
}

export function IlokUpgradeBanner() {
  const me = useSelector(meSelector)?.data
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(() => me === undefined || flagSet(dismissedKey(me.id)))

  if (!me || dismissed) return null

  const dismiss = () => {
    setFlag(dismissedKey(me.id))
    setDismissed(true)
  }

  return (
    <div className="ilok-upgrade-banner" role="note">
      {/* the black wordmark on an off-white chip — the same mark the Licenses
          page uses for iLok-bound entries (a light icon variant doesn't exist) */}
      <span className="ilok-upgrade-banner-icon" aria-hidden="true">{ilokIconBlack}</span>
      <span className="ilok-upgrade-banner-text">
        <strong>Own iLok licenses for Newfangled Audio plug-ins?</strong> Link your iLok
        account first — the matching native licenses are added to your account
        automatically and lower these upgrade prices.
      </span>
      <div className="ilok-upgrade-banner-actions">
        <button className="btn-upgrade" onClick={() => navigate("/account", { state: LINK_ILOK_STATE })}>
          Link iLok account
        </button>
        <button className="ilok-upgrade-banner-dismiss" onClick={dismiss} aria-label="Dismiss" title="Don't show again">
          &times;
        </button>
      </div>
    </div>
  )
}

// The one-time interruption on the Upgrade click. `onContinue` runs the
// caller's checkout; every way out marks the step seen.
export function IlokUpgradeConfirmDialog({ onContinue, onClose }: { onContinue: () => void, onClose: () => void }) {
  const me = useSelector(meSelector)?.data
  const navigate = useNavigate()
  const dialogRef = useRef<HTMLDialogElement>(null)
  // set at open, not on close: a navigation away unmounts the dialog
  // without a close event
  useEffect(() => {
    if (me) setFlag(confirmSeenKey(me.id))
    dialogRef.current?.showModal()
  }, [me])

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()}>&times;</button>
        <h2 className="redeem-dialog-title">Before you upgrade</h2>
        <p className="generate-codes-hint">
          If you hold iLok licenses for any plug-in in this bundle, link your iLok
          account first — their native licenses are added to your account
          automatically and this upgrade gets cheaper. You can always upgrade
          afterwards.
        </p>
        <div className="redeem-actions">
          <button onClick={() => { dialogRef.current?.close(); onContinue() }}>Continue to checkout</button>
          <button className="btn-upgrade" onClick={() => navigate("/account", { state: LINK_ILOK_STATE })}>
            Link iLok account
          </button>
        </div>
      </div>
    </dialog>
  )
}
